import { Alert, Button, InputField } from '@design-system';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import classNames from 'classnames';
import { motion, useReducedMotion } from 'framer-motion';
import type {
  GetStaticPropsContext,
  GetStaticPropsResult,
  InferGetStaticPropsType,
} from 'next';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import React, {
  CSSProperties,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';

import {
  LazyCloudflareTurnstile,
  useResetTurnstile,
} from '../../../components/Captcha/LazyCloudflareTurnstile';
import StickyHeader from '../../../components/Containers/StickyHeader';
import { Image } from '../../../components/Image';
import Layout from '../../../components/Layout';
import ScrollAnchor from '../../../components/ScrollAnchor';
import { type ImageSizes } from '../../../helpers/images';
import { imageOgStaticLoader } from '../../../helpers/og-image';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { Franchises, HeroImage } from '../../../types/Franchises';
import {
  GeneratedHubspotLists,
  useHubspotListMutation,
} from '../../../types/generated';
import { AssetStoryblok } from '../../../types/generated-storyblok';
import {
  getStoryblokPage,
  StoryResponse,
  useStoryblok,
} from '../../../utils/storyblok';
import { pageTranslations } from '../../../utils/translations';

const scrollToShortcut = (ref: React.RefObject<HTMLElement>) => {
  ref?.current?.scrollIntoView({
    behavior: isPrefersReducedMotion() ? 'instant' : 'smooth',
  });
};

type PageProps = {
  locale: string;
  locales: string[];
  ogImageUrl: string;
  preview: boolean;
  story: Franchises;
  backgroundImage: StoryblokImage;
};

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<PageProps>> {
  const translations = await pageTranslations(context.locale || 'en', [
    'reports',
  ]);

  const page: StoryResponse<Franchises> = await getStoryblokPage(
    'cdn/stories/insights/articles/collector-report-2025',
    {
      locale: context.locale,
      preview: context.preview,
    },
  );

  if (!page) {
    return {
      notFound: true,
    };
  }

  const story = page.data?.story;
  const heroPicker = story.content.heroPicker[0] as HeroImage;
  const backgroundImage = heroPicker.image;

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: story.content.title,
    imageSrc: backgroundImage.filename,
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
  });

  return {
    props: {
      locale: context.locale || 'en',
      locales: context.locales || [],
      preview: Boolean(context.preview),
      ogImageUrl,
      story,
      backgroundImage,
      ...translations,
    },
  };
}

const SectionColumns = ({
  children,
  className,
}: PropsWithChildren<{
  className?: string;
}>) => {
  return (
    <section
      className={classNames(
        'w-full md:max-w-3xl md:px-website lg:max-w-5xl xl:max-w-7xl 2xl:max-w-website',
        className,
      )}
    >
      <div className="flex w-full flex-col gap-4 md:flex-row">{children}</div>
    </section>
  );
};

const Separator = ({
  className,
  orientation = 'horizontal',
}: {
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}) => {
  return (
    <hr
      className={classNames(
        'relative flex items-center justify-center overflow-visible',
        'before:relative before:bg-divider/20',
        'after:absolute after:z-10 after:block after:h-[12px] after:w-[12px] after:rotate-45 after:bg-neutral-2 after:outline after:outline-4 after:outline-white',
        {
          'h-px w-full': orientation === 'horizontal',
          'h-px w-full md:h-auto md:w-auto': orientation === 'vertical',
          'before:h-full before:w-full': orientation === 'horizontal',
          'before:h-full before:w-full before:md:h-full before:md:w-px':
            orientation === 'vertical',
          'after:md:opacity-0': orientation === 'vertical',
        },
        className,
      )}
    />
  );
};

const Block = ({
  children,
  className,
}: PropsWithChildren<{
  className?: string;
}>) => {
  return (
    <article className={classNames('relative z-0 mx-auto w-full', className)}>
      {children}
    </article>
  );
};

const BlockHeadline = ({
  className,
  overline,
  title,
}: {
  title: string;
  overline: string;
  className?: string;
}) => {
  return (
    <Block
      className={classNames(
        'flex flex-col gap-3 overflow-hidden px-5 py-16 md:px-16',
        className,
      )}
    >
      <span className="mx-auto w-full max-w-xl text-pretty text-center">
        {overline}
      </span>
      <h2 className="mx-auto w-full max-w-xl text-balance text-center text-[2.5rem] font-bold leading-none tracking-tight md:text-[3.25rem] lg:text-[4.25rem]">
        {title}
      </h2>
    </Block>
  );
};

const BlockText = ({
  className,
  number,
  text,
  title,
}: {
  number: number;
  title: string;
  text: string | JSX.Element;
  className?: string;
}) => {
  return (
    <Block
      className={classNames(
        'flex max-w-120 flex-col px-5 py-12 sm:max-w-160 md:px-12',
        className,
      )}
    >
      <span className="pointer-events-none absolute -right-4 top-2 h-[15rem] min-w-[15rem] text-center text-[15rem] font-bold normal-nums leading-none tracking-tight text-active-4/20">
        {number}
      </span>

      <div className="flex h-full min-h-48 w-full flex-col justify-between gap-4 md:min-h-72">
        <h3 className="w-full max-w-sm text-balance text-md font-bold md:text-lg">
          {title}
        </h3>

        <div className="flex w-full max-w-sm flex-col gap-3 text-pretty">
          {typeof text === 'string' ? <p>{text}</p> : text}
        </div>
      </div>
    </Block>
  );
};

const BlockSuperQuote = ({
  caption,
  className,
  image,
  text,
}: {
  image: string;
  text: string;
  caption: string;
  className?: string;
}) => {
  return (
    <Block
      className={classNames(
        'flex max-w-120 flex-col items-center justify-center gap-4 px-5 py-8 sm:max-w-160 md:max-w-full md:px-0',
        className,
      )}
    >
      <div className="flex w-full items-start justify-center gap-8 rounded-md bg-active-1 p-8 max-sm:flex-col md:py-16">
        <div className="flex aspect-[1/1] w-16 flex-col items-center justify-center overflow-hidden rounded-full bg-neutral-1 text-center">
          <Image
            alt={caption || ''}
            className="h-full w-full object-cover"
            src={image}
            width={96}
          />
        </div>
        <figure className="flex w-full max-w-xl flex-col items-center justify-center gap-3">
          <blockquote className="w-full text-balance text-left -indent-[5px] md:-indent-[7px] md:text-md">
            {text}
          </blockquote>
          <figcaption className="w-full text-balance text-left text-active-4">
            {caption}
          </figcaption>
        </figure>
      </div>
    </Block>
  );
};

const BlockQuote = ({
  caption,
  className,
  text,
}: {
  text: string;
  caption: string;
  className?: string;
}) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <Block
      className={classNames(
        'flex max-w-120 flex-col items-center justify-center gap-4 overflow-hidden px-5 py-12 sm:max-w-160 md:px-12',
        className,
      )}
    >
      <motion.figure
        className="flex w-full flex-col items-center justify-center gap-3"
        initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          y: shouldReduceMotion ? 0 : 100,
        }}
        viewport={{ once: true }}
        whileInView={{
          opacity: 1,
          y: 0,
          transition: {
            type: 'easeOut',
            duration: 0.8,
            delay: 0.2,
          },
        }}
      >
        <blockquote className="w-full max-w-md text-balance text-center md:text-md">
          {text}
        </blockquote>
        <figcaption className="w-full max-w-md text-balance text-center text-active-4">
          {caption}
        </figcaption>
      </motion.figure>
    </Block>
  );
};

const BlockImage = ({
  aspect,
  caption,
  className,
  image,
}: {
  image: AssetStoryblok;
  caption?: string;
  aspect: ImageSizes;
  className?: string;
}) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <Block
      className={classNames(
        'flex flex-col items-center justify-center gap-4 px-5 py-8 md:px-0',
        className,
      )}
    >
      <motion.figure
        className="flex w-full flex-col items-center justify-center gap-3"
        initial={{ opacity: shouldReduceMotion ? 1 : 0 }}
        viewport={{ once: true }}
        whileInView={{
          opacity: 1,
          transition: {
            type: 'easeOut',
            duration: 0.8,
          },
        }}
      >
        <div
          className="flex aspect-[var(--aspect-sm)] w-full flex-col items-center justify-center overflow-hidden rounded-md bg-neutral-1 text-center md:aspect-[var(--aspect-md)] lg:aspect-[var(--aspect-lg)]"
          style={
            {
              '--aspect-sm': aspect.sm.ratio,
              '--aspect-md': aspect.md?.ratio || aspect.sm.ratio,
              '--aspect-lg':
                aspect.lg?.ratio || aspect.md?.ratio || aspect.sm.ratio,
            } as CSSProperties
          }
        >
          <Image
            alt={image.alt || caption || ''}
            className="h-full w-full object-cover"
            focus={image.focus}
            size={aspect}
            src={image.filename}
          />
        </div>
        {caption ? (
          <figcaption className="w-full max-w-md text-balance text-center text-xs text-neutral-4">
            {caption}
          </figcaption>
        ) : null}
      </motion.figure>
    </Block>
  );
};

const getConicGradient = ({
  colors,
  data,
  totalValue,
}: {
  colors: string[];
  data: {
    label: string;
    value: number;
  }[];
  totalValue: number;
}) => {
  const gradient: string[] = [];
  let cumulativePercent = 0;

  data.forEach((item, index) => {
    const { value } = item;
    const percentage = Math.floor(
      totalValue > 0 ? (value / totalValue) * 100 : 0,
    );
    const nextCumulativePercent = cumulativePercent + percentage;
    gradient.push(
      `${colors[index]} ${cumulativePercent}% ${nextCumulativePercent}%`,
    );
    cumulativePercent = nextCumulativePercent;
  });

  return `conic-gradient(${gradient.join(', ')})`;
};

const PieChart = ({
  data,
}: {
  data: {
    label: string;
    value: number;
  }[];
}) => {
  const shouldReduceMotion = useReducedMotion();

  const colors = [
    '#7F200F',
    '#FF5207',
    '#FFCEA8',
    '#F7F6F5',
    '#BFD7FE',
    '#2570EB',
  ];

  return (
    <>
      <motion.div
        className="aspect-[1/1] w-full rounded-full"
        initial={{
          opacity: 1,
          background: getConicGradient({
            colors,
            data,
            totalValue: shouldReduceMotion ? 100 : 0,
          }),
        }}
        transition={{
          ease: 'easeOut',
          duration: 0.4,
        }}
        viewport={{ once: true }}
        whileInView={{
          opacity: 1,
          background: getConicGradient({ colors, data, totalValue: 100 }),
        }}
      />

      <div className="flex w-full flex-col divide-y divide-divider/20 md:max-w-sm">
        {data.map(({ label, value }, index) => (
          <div
            key={`piechart-${label}`}
            className="flex min-h-9 items-center gap-3"
          >
            <span
              className="h-3 w-3 rounded-full"
              style={{ 'backgroundColor': colors[index] }}
            />
            <span className="flex-1">{label}</span>
            <span className="shrink-0 text-neutral-4">{value}%</span>
          </div>
        ))}
      </div>
    </>
  );
};

const BarChart = ({
  data,
  rowClassName,
}: {
  data: {
    label: string;
    value: number;
  }[];
  rowClassName?: string;
}) => {
  const shouldReduceMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setIsMobile(entry.contentRect.width <= 448);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={classNames(
        'flex w-full flex-col items-center justify-center gap-4',
        isMobile ? 'gap-4' : 'gap-2',
      )}
    >
      {data.map((item, index) => {
        const maxValue = Math.max(...data.map((value) => value.value));

        return (
          <div
            key={`barchart-${item.label}`}
            className={classNames(
              'flex w-full',
              isMobile ? 'flex-col gap-2' : 'flex-row items-center gap-3',
            )}
          >
            <span
              className={classNames(
                'text-balance text-xs text-neutral-4',
                isMobile ? 'w-full' : 'w-[20%] shrink-0',
              )}
            >
              {item.label}
            </span>
            <motion.span
              className="w-full overflow-x-clip"
              initial="hidden"
              viewport={{ once: true }}
              whileInView="visible"
            >
              <motion.span
                className={classNames(
                  'flex items-center justify-end rounded-sm bg-active-4 px-2 font-bold text-white sm:px-3',
                  rowClassName,
                )}
                style={{ width: `${item.value * (100 / maxValue)}%` }}
                transition={{
                  ease: 'easeOut',
                  duration: 0.4,
                  delay: index * 0.1,
                }}
                variants={{
                  hidden: {
                    opacity: shouldReduceMotion ? 1 : 0,
                    x: shouldReduceMotion ? 0 : '-100%',
                  },
                  visible: { opacity: 1, x: 0 },
                }}
              >
                {`${item.value}%`}
              </motion.span>
            </motion.span>
          </div>
        );
      })}
    </div>
  );
};

const BlockGraph = ({
  caption,
  children,
  className,
}: PropsWithChildren<{
  caption: string;
  className?: string;
}>) => {
  return (
    <Block
      className={classNames(
        'flex flex-col items-center justify-center gap-4 overflow-hidden px-5 py-8 md:px-0',
        className,
      )}
    >
      <figure className="flex w-full max-w-5xl flex-col items-center justify-center gap-8">
        <figcaption className="w-full max-w-md text-balance text-center text-active-4">
          {caption}
        </figcaption>

        {children}
      </figure>
    </Block>
  );
};

const SubscribeForm = forwardRef((_, ref) => {
  const { t } = useTranslation(['reports']);
  const [captchaToken, setCaptchaToken] = useState('');
  const { turnstileRef, turnstileReset } = useResetTurnstile();
  const [hubspotListMutation] = useHubspotListMutation();

  const form = useForm<{ email: string }>({
    criteriaMode: 'firstError',
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: { email: '' },
  });

  const { isValid } = form.formState;
  const [isLoading, setLoading] = useState(false);
  const [isSuccess, setSuccess] = useState(false);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      await hubspotListMutation({
        variables: {
          email: form.getValues().email,
          captcha: captchaToken,
          list: GeneratedHubspotLists.Whitepaper2025,
        },
      });
      setSuccess(true);
      form.reset();
    } catch (err) {
      setSuccess(false);
      turnstileReset();
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hubspotListMutation, captchaToken, form, turnstileReset]);

  useImperativeHandle(ref, () => ({
    focusEmailInput: () => {
      form.setFocus('email');
    },
  }));

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4">
      {isSuccess && (
        <Alert status="success">
          {t('reports:subscription.alert.success')}
        </Alert>
      )}

      <div className="w-full">
        <div className="flex w-full flex-col gap-3 md:flex-row">
          <InputField
            id="email"
            placeholder={t('reports:subscription.email.placeholder')}
            type="email"
            {...form.register('email', {
              required: true,
              pattern: /\S+@\S+\.\S+/,
            })}
          />

          <div className="md:shrink-0">
            <Button
              disabled={!isValid || isLoading}
              fullWidth
              label={t('reports:subscription.ctaLabel.default')}
              loading={isLoading}
              onClick={() => void handleSubmit()}
              size="md"
              variant="primary"
            />
          </div>
        </div>

        <LazyCloudflareTurnstile
          actionName="collector_report_subscribe"
          className="mt-4 flex w-full items-center justify-center"
          onError={() => setCaptchaToken('')}
          onExpire={() => setCaptchaToken('')}
          onSuccess={setCaptchaToken}
          turnstileRef={turnstileRef}
        />
      </div>
    </div>
  );
});

const Page = ({
  backgroundImage,
  locale,
  locales,
  ogImageUrl,
  preview,
  story,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const page = useStoryblok(story, preview, locale);

  const { t } = useTranslation(['reports']);
  const isMobile = useMediaQuery('sm');

  const ctaRef = useRef<HTMLAnchorElement>(null);
  const sectionSubscriptionRef = useRef<HTMLAnchorElement>(null);
  const subscribeFormRef = useRef<{ focusEmailInput: () => void } | null>(null);

  const handleScrollTo = useCallback(() => {
    if (sectionSubscriptionRef.current) {
      scrollToShortcut(sectionSubscriptionRef);
      setTimeout(() => {
        subscribeFormRef.current?.focusEmailInput();
      }, 600);
    }
  }, []);

  const images = story.content.article
    .filter((blocks) => {
      return blocks.component === 'multiImage';
    })
    .map((blocks) => {
      return blocks.images
        .filter((block) => {
          return block.component === 'image';
        })
        .map((block) => {
          return {
            image: block.image,
            caption: block.caption?.content?.[0].content?.[0].text,
          };
        })[0];
    });

  const [toastIsOpened, setToastIsOpened] = useState(false);

  useEffect(() => {
    if (toastIsOpened) {
      setTimeout(() => {
        setToastIsOpened(false);
      }, 5000);
    }
  }, [toastIsOpened]);

  return (
    <Layout
      description={page.content.seo.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={page.content.seo.title}
    >
      <StickyHeader ctaRef={ctaRef}>
        <div className="flex w-full items-center space-x-3">
          <div className="flex w-full flex-col overflow-hidden">
            <p className="line-clamp-2 text-balance text-neutral-4">
              {page.content.title}
            </p>
          </div>
          <div className="shrink-0">
            <Button
              label={t('reports:cta.sticky')}
              onClick={handleScrollTo}
              size={isMobile ? 'sm' : 'md'}
              variant="primary"
            />
          </div>
        </div>
      </StickyHeader>

      <div className="flex w-full flex-col gap-9 overflow-x-hidden">
        <div className="mx-auto flex w-full flex-col items-center lg:pt-16">
          <div className="wrapper-website flex flex-col gap-24 py-16">
            <div className="flex w-full flex-col gap-12">
              <span className="text-center text-neutral-4">
                Collector Report · March 2025
              </span>
              <h1 className="mx-auto w-full text-balance text-center text-[2.5rem] font-bold leading-none tracking-tight md:text-[3.25rem] lg:text-[4.25rem]">
                Nearly 90% of new generation collectors have the means and
                desire to donate more to museums
              </h1>
            </div>

            <div className="theme-dark relative flex aspect-[2/3] w-full flex-col items-center justify-end overflow-hidden rounded-md bg-neutral-6 px-9 py-9 text-white md:aspect-[4/3] md:items-start lg:aspect-[16/9]">
              <div className="absolute inset-0">
                <Image
                  alt="Caption"
                  className="h-full w-full object-cover"
                  size={{
                    sm: { ratio: '2/3', span: 12 },
                    md: { ratio: '4/3', span: 12 },
                    lg: { ratio: '16/9', span: 12 },
                  }}
                  src={backgroundImage.filename}
                />
              </div>
              <span className="absolute inset-0 bg-neutral-6/40 md:bg-neutral-6/20" />
              <span className="absolute inset-0 bg-neutral-6/40 backdrop-blur-3xl [maskImage:linear-gradient(0deg,rgba(0,0,0,1)_25%,rgba(0,0,0,0)_50%)] md:[maskImage:linear-gradient(90deg,rgba(0,0,0,1)_10%,rgba(0,0,0,0)_50%)]" />

              <div className="relative flex w-full max-w-xs flex-col items-center gap-6 md:items-start">
                <div className="flex w-full flex-col gap-3">
                  <p className="text-pretty text-center drop-shadow-lg md:text-left">
                    Our 3.5 million strong community of art enthusiasts and
                    collectors stretches from Sydney to Seattle, Seoul to
                    Santiago.
                  </p>
                  <p className="text-pretty text-center drop-shadow-lg md:text-left">
                    Through in-depth interviews and surveys of over 3,100
                    members, we’ve built up a picture of the new generation.
                  </p>
                </div>
                <div className="shrink-0">
                  <Button
                    label={t('reports:cta.default')}
                    onClick={handleScrollTo}
                    variant="primary"
                  />

                  <ScrollAnchor ref={ctaRef} id="heroRef" />
                </div>
              </div>
            </div>
          </div>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <Block className="flex max-w-120 flex-col px-5 py-16 sm:max-w-160 md:max-w-full md:px-0">
              <div className="mx-auto flex w-full max-w-lg flex-col gap-3 text-pretty">
                <p className="text-pretty">
                  Avant Arte was built around a new generation of collectors,
                  whose energy to discover, collect and be inspired by art gives
                  us optimism for the future. This confidence is a scarcity
                  today given the scale of challenges the art world continues to
                  face after two turbulent years.
                </p>
                <p className="text-pretty">
                  Last year we published our{' '}
                  <Link
                    className="underline underline-offset-2 hover:no-underline"
                    href="https://avantarte.com/insights/articles/collector-report-2024"
                    target="_blank"
                    title="Collector Report 2024"
                  >
                    first report
                  </Link>{' '}
                  to better understand and harness the enthusiasm of this new
                  generation. One finding in particular stood out: while 40%
                  want to do more to support museums financially, 75% didn’t
                  know how. While the stewardship pathway from membership to
                  major giving may seem self-evident for anyone within the art
                  world, its apparent opacity for these new arts audiences was
                  startling. And the lack of understanding cuts both ways. Many
                  museums suffer the same lack of visibility around the new
                  generation.
                </p>
                <p className="text-pretty">
                  That’s why we have placed the question of how the new
                  generation views and engages with museums today at the heart
                  of our new report, structured around five key drivers of
                  institutional funding:
                </p>
                <ul className="flex list-outside list-['—'] flex-col gap-3 pl-4 marker:text-active-4">
                  <li className="text-pretty pl-3">
                    <strong>Programming:</strong> blockbuster exhibitions need
                    to be paired with shows featuring new talent to keep the new
                    generation coming back. 85% want to see more exhibitions by
                    emerging artists, while 70% want museums to take more risks.
                  </li>
                  <li className="text-pretty pl-3">
                    <strong>Promotion:</strong> despite following museums on
                    social media in their droves, with most of our community
                    following at least ten on Instagram, many cite a lack of
                    digital promotion as the main barrier to attendance.
                  </li>
                  <li className="text-pretty pl-3">
                    <strong>Membership:</strong> 42% feel memberships lack
                    value, with 75% calling for more in-person events as part of
                    membership benefits.
                  </li>
                  <li className="text-pretty pl-3">
                    <strong>Retail:</strong> spending is held back by two
                    misconceptions: that museum shops are only for souvenirs,
                    and that they are not a credible way of contributing funds.
                    Disabuse the new gen of these and they are 4x more likely to
                    spend $500+ per purchase.
                  </li>
                  <li className="text-pretty pl-3">
                    <strong>Individual giving:</strong> the new generation are
                    bombarded by donation requests, with over 40% asked for a
                    new donation at least once a month. Innovative fundraising
                    models can help museums stand out, with fundraising editions
                    cited as the most popular method of giving.
                  </li>
                </ul>
                <p className="text-pretty">
                  We have seen first-hand the impact for institutions from this
                  new generation, by the millions of dollars they have
                  contributed through our fundraising editions. Yet, their
                  potential goes further. Our longer-term vision is that new
                  audiences and collectors can develop into a diverse base of
                  members, consistent individual donors and some into the most
                  impactful major philanthropists over time: transforming this
                  new generation of collectors into the greatest generation of
                  patrons.
                </p>
                <p className="mt-6 text-pretty text-center text-active-4">
                  Mazdak Sanii, CEO and Co-Founder, Avant Arte
                </p>
              </div>
            </Block>
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockHeadline
              overline="Who are they?"
              title="A New Generation of Patrons"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns className="py-16">
            <BlockGraph caption="Age demographics" className="mx-auto w-full">
              <div className="flex w-full max-w-sm flex-col items-center gap-8 md:max-w-5xl md:flex-row">
                <PieChart
                  data={[
                    {
                      label: '18-29',
                      value: 1.7 + 7.4,
                    },
                    {
                      label: '30-39',
                      value: 10.1 + 14,
                    },
                    {
                      label: '40-49',
                      value: 16.5 + 14.9,
                    },
                    {
                      label: '50-59',
                      value: 11 + 8.6,
                    },
                    {
                      label: '60+',
                      value: 6 + 8.5,
                    },

                    {
                      label: 'Prefer not to say',
                      value: 1.4,
                    },
                  ]}
                />
              </div>
            </BlockGraph>
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockText
              number={1}
              text="It’s clear that the grey-haired, blazer-wearing art connoisseur stereotype is continuing to fade. Half of our cohort are under 40, with three quarters under 55. Over 40% have an annual income of over $100k; a fifth earn over $200k. They’re certainly willing to spend on art, with over a third investing more than €10,000 annually and almost two thirds investing over €5,000."
              title="Over a third invest more than €10,000 annually in art, almost two thirds invest over €5,000"
            />

            <Separator orientation="vertical" />

            <BlockQuote
              caption="Male collector, 35-39, USA"
              text="“The main reason I&nbsp;give is because I&nbsp;think the institution in question deserves support, but it’s nice when I&nbsp;get something.”"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockText
              number={2}
              text="The new generation see museums as a force of social good, with an expansive understanding of the benefits they bring. 92% strongly believe that museums should work hard to cater for everyone in society, which ties directly to their views on public funding – with 67% in favour of financial support for museums."
              title="86% of the new-gen believe that museums are vital to a healthy society"
            />

            <Separator orientation="vertical" />

            <BlockText
              number={3}
              text="Only 3% of traditional art buyers cite philanthropy as a motivation, but the new generation see collecting art as an inherently generous thing to do."
              title="Over half say supporting artists and institutions is a major reason for buying art"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockSuperQuote
              caption="Jessica Morgan, Director of Dia Foundation"
              image="https://images.avantarte.org/media/2uRHxuxVtDdb6SwBZMazJDKaJtd"
              text="“Dia has long been committed to fostering deep, lasting relationships between audiences and the arts. This report underscores the urgency of engaging the next generation—not only as visitors but as active supporters of institutions and artists. The findings highlight both a challenge and an opportunity: younger audiences are eager to contribute, yet traditional philanthropy pathways remain unclear to them. At&nbsp;Dia, we have seen firsthand the impact of innovative fundraising models, such as our recent collaboration with George Condo and Avant Arte. The future of institutional sustainability depends on bridging this gap—reimagining how we connect, cultivate, and inspire this new generation to become the patrons of tomorrow.”"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns className="py-16">
            <BlockImage
              aspect={{
                sm: { ratio: '1/1', span: 12 },
                md: { ratio: '4/3', span: 12 },
                lg: { ratio: '16/9', span: 12 },
              }}
              caption={images?.[0].caption}
              className="max-w-120 sm:max-w-160 md:max-w-full"
              image={images?.[0].image}
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockHeadline
              overline="Programming and promotion"
              title="What gets the new generation through the door?"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockText
              number={4}
              text="While blockbuster exhibitions remain a powerful draw, with 86% citing specific exhibitions as a key motivation for visiting museums, appetite for up-and-coming voices is also strong. 85% believe that museums should balance established names with young and emerging artists, and 69% would actively encourage museums to take greater risks in their programming choices."
              title="85% believe museums should balance established and emerging artists"
            />

            <Separator orientation="vertical" />

            <BlockQuote
              caption="Female collector, 35-39, USA"
              text="“You need big-name exhibitions to get people through the door. And you need exciting, emerging artists to remain culturally dynamic. It’s the yin and yang of any good museum.”"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns className="py-16">
            <BlockImage
              aspect={{
                sm: { ratio: '3/4', span: 12 },
                md: { ratio: '3/4', span: 6 },
                lg: { ratio: '3/4', span: 4 },
              }}
              caption={images?.[1].caption}
              className="mx-auto max-w-xl"
              image={images?.[1].image}
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockQuote
              caption="Male collector, 30-34, Spain"
              text="“I&nbsp;don’t see content from half the accounts I&nbsp;follow. Maybe they just never post? I’m not sure.”"
            />

            <Separator orientation="vertical" />

            <BlockText
              number={5}
              text="New-gen collectors get most of their art news and information online, especially through the accounts of their favourite artists, galleries and institutions. 83% follow at least 5 artists on social media, 70% follow more than 10, and nearly half track over 20 artists. A not insignificant 16% follow over 100 artists."
              title="Nearly 20% follow over 100 artists and institutions on Instagram"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockText
              number={6}
              text="Despite being heavily online, 70% of respondents indicated insufficient digital presence was a key barrier to physical attendance to galleries and museums, more than any other factor. Given the number of art accounts they follow, this appears to be a problem of content not being algorithmically-friendly enough to reach them, rather than one of followership."
              title="Yet 70% identified insufficient online presence as the key barrier"
            />

            <Separator orientation="vertical" />

            <BlockQuote
              caption="Female collector, 40-44, UK"
              text="“I&nbsp;find I&nbsp;have to work quite hard to find out what’s going on in London. If I&nbsp;miss just one email, I&nbsp;can easily miss a whole exhibition.”"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns className="py-16">
            <BlockGraph
              caption="What are the biggest barriers to museums or galleries being accessible to a broader audience?"
              className="max-w-120 sm:max-w-160 md:max-w-full"
            >
              <BarChart
                data={[
                  {
                    label: 'Insufficient online presence or comms about events',
                    value: 70,
                  },
                  {
                    label: 'Lack of affordable / free exhibitions',
                    value: 66,
                  },
                  {
                    label: 'Limited physical or geographical access',
                    value: 61,
                  },
                  {
                    label:
                      'Lack of diverse or relevant exhibitions and programming',
                    value: 60,
                  },
                  {
                    label: 'Inconvenient hours',
                    value: 54,
                  },
                  {
                    label: 'Lack of family-friendly options',
                    value: 41,
                  },
                ]}
                rowClassName="md:min-h-12 min-h-8"
              />
            </BlockGraph>
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockHeadline
              overline="Memberships and Museum Shops"
              title="Why are funds being left on the table?"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockText
              number={7}
              text={
                <>
                  <p className="text-pretty">
                    Currently, roughly half of new-gen collectors are members of
                    museums and galleries. Of those who are members, 42% don’t
                    think they’re getting enough value for money.
                  </p>
                  <p className="text-pretty">So, how can we remedy this?</p>
                  <p className="text-pretty">
                    Overwhelmingly, the new-gen want in-person events and
                    experiences that transport them from digital spaces into the
                    living, breathing art world, with a strong desire for
                    unlimited free admission and early access to exhibitions or
                    events.
                  </p>
                </>
              }
              title="42% don’t think they’re getting enough value for money from membership"
            />

            <Separator orientation="vertical" />

            <BlockText
              number={8}
              text={
                <>
                  <p className="text-pretty">
                    Spending at the museum shop is hampered by two unhelpful
                    ideas – the belief that museum shops are only for buying
                    souvenirs, and that spending in the shop is not a way of
                    supporting the museum.
                  </p>
                  <p className="text-pretty">
                    These beliefs directly affect how much collectors spend.
                    Almost half of those who see museum shops as a place for
                    souvenirs have never spent more than $100 in one go, while
                    only 15% have ever spent over $500 on a museum purchase. By
                    contrast, amongst those who believe that the shops have more
                    to offer, this figure rises to 60%.
                  </p>
                </>
              }
              title="59% don't see purchasing from the shop as a way of supporting the museum"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockImage
              aspect={{
                sm: { ratio: '1/1', span: 12 },
                md: { ratio: '1/1', span: 6 },
                lg: { ratio: '1/1', span: 4 },
              }}
              caption={images?.[2].caption}
              className="max-w-120 max-md:!pb-2 sm:max-w-160 md:max-w-full"
              image={images?.[2].image}
            />
            <BlockImage
              aspect={{
                sm: { ratio: '1/1', span: 12 },
                md: { ratio: '1/1', span: 6 },
                lg: { ratio: '1/1', span: 4 },
              }}
              caption={images?.[3].caption}
              className="max-w-120 max-md:!pt-2 sm:max-w-160 md:max-w-full"
              image={images?.[3].image}
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockGraph
              caption="What have you bought at the gift shop?"
              className="max-w-120 px-5 sm:max-w-160 md:px-12"
            >
              <BarChart
                data={[
                  {
                    label: 'Books',
                    value: 68,
                  },
                  {
                    label: 'Prints',
                    value: 50,
                  },
                  {
                    label: 'Catalogues',
                    value: 48,
                  },
                  {
                    label: 'Postcards',
                    value: 48,
                  },
                  {
                    label: 'Merchandise',
                    value: 42,
                  },
                  {
                    label: 'Posters',
                    value: 38,
                  },
                ]}
                rowClassName="min-h-8"
              />
            </BlockGraph>

            <Separator orientation="vertical" />

            <BlockQuote
              caption="Female collector, 25-29, USA"
              text="“I&nbsp;know my membership supports the museum, but I’d never really thought about spending money in the shop in the same way.”"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockHeadline
              overline="Individual Giving"
              title="What stops the new generation from donating more to museums?"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockText
              number={9}
              text="The new generation want and are able to give more. Yet requests for their money exist within a highly competitive landscape of giving, in which the arts are being pushed out by other causes. 75% get asked by charities or other causes to donate more than twice a year; 42% get asked once a month; 24% once a week; and 15% several times a week or daily."
              title="88% have the means to donate more and 83% have the appetite to up their support"
            />

            <Separator orientation="vertical" />

            <BlockQuote
              caption="Male collector, 30-35, UK"
              text="“I’m too busy to evaluate every request for money in depth, so I&nbsp;tend to just give when it feels right.”"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockSuperQuote
              caption="Sarah Arison, Board President, The Museum of Modern Art"
              image="https://images.avantarte.org/media/2uRHxuw4KnMrv2GSeln6LQ8obaJ"
              text="“The scale of opportunity for museums to better engage the new generation is only matched by the urgency to do so. These are the patrons of tomorrow, and yet the stewardship path - from visitor to member and benefit editions collector through to major donor - will only be made possible through an in-depth understanding of their beliefs and attitudes towards museums today. These findings provide a vital note of optimism and the report is an important contribution to that endeavour.”"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockImage
              aspect={{
                sm: { ratio: '1/1', span: 12 },
                md: { ratio: '1/1', span: 12 },
                lg: { ratio: '1/1', span: 12 },
              }}
              caption={images?.[4].caption}
              className="max-w-120 px-5 sm:max-w-160 md:px-12"
              image={images?.[4].image}
            />

            <Separator orientation="vertical" />

            <BlockText
              number={10}
              text={
                <>
                  <p className="text-pretty">
                    Amongst the clamour of the fundraising sphere, institutions
                    need to find innovative ways of commanding the new
                    generation’s attention. Our research points to three clear
                    ways of doing this:
                  </p>
                  <ol className="flex list-outside list-decimal flex-col gap-3 pl-4 marker:text-active-4">
                    <li className="text-pretty pl-3">
                      Offer a tangible value exchange. 82% indicated they were
                      more likely to donate when offered benefits like
                      memberships or an artwork – more than any other answer.
                    </li>
                    <li className="text-pretty pl-3">
                      Tap into a potential donors’ personal connection to a
                      museum’s mission. 79% said this was an important factor in
                      their support.
                    </li>
                    <li className="text-pretty pl-3">
                      Connect a donor’s funds and its specific impact –
                      something that two thirds of respondents say would make
                      them more likely to donate.
                    </li>
                  </ol>
                </>
              }
              title="82% indicated they were more likely to donate when offered benefits like memberships or an artwork"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <SectionColumns>
            <BlockText
              number={11}
              text={
                <>
                  <p className="text-pretty">
                    When presented with a list of potential ways to give funds
                    to a museum, there’s one option that respondents picked more
                    than any other: a fundraising edition, sold in support of a
                    particular institution or cause.
                  </p>
                  <p className="text-pretty">
                    This is perhaps unsurprising in consideration of the factors
                    above. Fundraising editions tick all three boxes. Most
                    obviously, they offer a tangible value exchange – an artwork
                    back in return for a donation. They also leverage personal
                    connection, with over half of those who’ve purchased a
                    fundraising edition saying they’re more likely to support
                    their chosen institution again in the future.
                  </p>
                </>
              }
              title="Their preferred way of supporting a museum is a fundraising edition"
            />

            <Separator orientation="vertical" />

            <BlockQuote
              caption="Female collector, 30-34, UK"
              text="“It’s hard to forget about an institution when you’ve got a print you bought in support of it on your living room wall.”"
            />
          </SectionColumns>

          <Separator orientation="horizontal" />

          <div className="wrapper-website flex flex-col gap-12 py-16">
            <div className="theme-dark relative flex w-full flex-col items-center justify-center overflow-hidden rounded-md bg-neutral-6 px-6 py-36 text-white">
              <ScrollAnchor
                ref={sectionSubscriptionRef}
                id="SectionSubscription"
                offset={-72}
              />

              <div className="absolute inset-0">
                <Image
                  alt={images?.[5].caption || images?.[5].image.alt || ''}
                  className="h-full w-full object-cover"
                  size={{
                    sm: { ratio: '2/3', span: 12 },
                    md: { ratio: '4/3', span: 12 },
                    lg: { ratio: '16/9', span: 12 },
                  }}
                  src={images?.[5].image.filename}
                />
              </div>

              <span className="absolute inset-0 bg-neutral-6/80" />

              <div className="relative flex w-full flex-col gap-6 md:gap-9">
                <h4 className="mx-auto w-full max-w-xl text-balance text-center text-[2.5rem] font-bold leading-none tracking-tight md:text-[3.25rem] lg:text-[4.25rem]">
                  Download the full report
                </h4>
                <SubscribeForm ref={subscribeFormRef} />
                <p className="mx-auto w-full max-w-xl text-balance text-center text-neutral-3">
                  Collector Report · March 2025
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Page;
